/* eslint-disable @typescript-eslint/no-explicit-any */
import type { JSX } from 'react';
import React, { useContext, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';

import DashboardIcon from '@mui/icons-material/Dashboard';
import PhoneIcon from '@mui/icons-material/Phone';
import { Box } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import type { TFunction } from 'i18next';
import styled from 'styled-components';

import { StoreContext } from '../../store';
import { PAGE_ROUTES } from '../../utils/constants';
import Spinner from '../CargillLeafSpinner';

import './index.css';

interface HeaderResponsiveIconTextProps {
  icon: React.ComponentType<any>;
  text: React.ReactNode;
  iconProps?: Record<string, any>;
  textProps?: Record<string, any>;
  containerProps?: Record<string, any>;
}

function HeaderResponsiveIconText({
  icon: Icon,
  text,
  iconProps = {},
  textProps = {},
  containerProps = {},
  ...props
}: HeaderResponsiveIconTextProps): JSX.Element {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center' }} {...containerProps} {...props}>
      <span className="header-icon-mobile-pad">
        <Icon {...iconProps} />
      </span>
      <span className="header-icon-text-desktop" {...textProps}>
        {text}
      </span>
    </span>
  );
}

/* ----------------------------------
   Styles
---------------------------------- */

const headerIconTextStyle = `
  @media only screen and (max-width: 700px) {
    .header-icon-text-desktop { display: none; }
    .header-icon-mobile-pad { padding: 0; display: inline-flex; }
    .header-support-desktop { display: none; }
    .header-support-mobile { display: inline-flex; }
    .header-dashboard-desktop { display: none; }
    .header-dashboard-mobile { display: inline-flex; }
  }
  @media only screen and (min-width: 701px) {
    .header-icon-mobile-pad { padding: 0; margin-right: 0; }
    .header-support-mobile { display: none; }
    .header-dashboard-mobile { display: none; }
  }
`;

const Wrapper = styled.div`
  width: fit-content;
  display: flex;
  align-items: center;
  pointer-events: auto;
  padding-top: 5px;
  padding-right: 5px;
`;

const TopLayer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding: 0;
  background: transparent;
`;

export const SavingMessage = styled.p`
  color: var(--neutral-1000, #101c16) !important;
  margin-left: 8px;
  font: var(--sprout-font-body1-bold);
  text-decoration: none;
`;

const SaveBanner = styled.div`
  display: flex;
  align-items: center;
`;

const SupportLink = styled(Link)`
  color: var(--neutral-1000, #101c16) !important;
  margin-right: 0;
  font: var(--sprout-font-body1-bold);
  text-decoration: none;
  transition: color 0.2s;

  &:hover {
    color: var(--brand-green-900, #012912) !important;
  }
`;

/* ----------------------------------
   Component
---------------------------------- */

interface BackToDashboardResponsiveProps {
  t: TFunction;
}

function BackToDashboardResponsive({ t }: BackToDashboardResponsiveProps) {
  return (
    <>
      <SupportLink className="header-dashboard-desktop" to="/secure/dashboard">
        {/* @ts-ignore next-line */}
        {t('buttons.backToDashboard')}
      </SupportLink>
      <IconButton
        className="header-dashboard-mobile"
        component={Link}
        to="/secure/dashboard"
        aria-label={t('buttons.backToDashboard')}
        size="large"
        sx={{
          color: 'var(--neutral-700, #404945) !important',
          p: 0,
          ml: 0
        }}
      >
        <DashboardIcon sx={{ fontSize: 24 }} />
      </IconButton>
    </>
  );
}

function FloatingHeader(): JSX.Element | null {
  const { pathname } = useLocation();
  const segments = pathname.replace(/\/$/, '').split('/').filter(Boolean);

  const currentPage =
    segments[0] === 'secure' && segments.length === 1 ? PAGE_ROUTES.dashboard : segments[segments.length - 1];

  const { t } = useTranslation();
  const { store } = useContext(StoreContext);

  const isSaving = store?.apiEvent?.isSaving ?? false;
  const [target, setTarget] = useState<HTMLElement | null>(null);

  useEffect(() => {
    const attachPortal = (): boolean => {
      const nav = document.querySelector('nav.appbar');
      const rightContainer = nav?.querySelector('.flex.flex-wrap.gap-sm');

      if (!rightContainer) return false;

      let container = rightContainer.querySelector<HTMLElement>('#floating-header-root');

      if (!container) {
        container = document.createElement('div');
        container.id = 'floating-header-root';
        rightContainer.prepend(container);
      }

      setTarget(container);
      return true;
    };

    if (attachPortal()) return;

    const observer = new MutationObserver(() => {
      if (attachPortal()) {
        observer.disconnect();
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });

    return () => observer.disconnect();
  }, []);

  const saveDisplay = isSaving ? (
    <SaveBanner>
      <HeaderResponsiveIconText
        icon={Spinner}
        iconProps={{ size: '24' }}
        text={<SavingMessage>{t('header.autosaving')}</SavingMessage>}
      />
    </SaveBanner>
  ) : null;

  const helpPath = `/${PAGE_ROUTES.help}`;

  function SupportLinkResponsive(): JSX.Element {
    return (
      <>
        <SupportLink
          className="header-support-desktop"
          data-event="common-click"
          data-metrics-desc="Help"
          data-metrics-type="LINK"
          to={helpPath}
        >
          {t('header.contactSupport')}
        </SupportLink>

        <IconButton
          className="header-support-mobile"
          component={Link}
          to={helpPath}
          aria-label={t('header.contactSupport')}
          size="large"
          sx={{
            color: 'var(--neutral-700, #404945) !important',
            p: 0,
            ml: 0
          }}
          data-event="common-click"
          data-metrics-desc="Help"
          data-metrics-type="LINK"
        >
          <PhoneIcon sx={{ fontSize: 24 }} />
        </IconButton>
      </>
    );
  }

  const content = (
    <>
      <style>{headerIconTextStyle}</style>

      <Wrapper>
        <TopLayer>
          <Box display="flex" alignItems="center" sx={{ gap: '16px', pointerEvents: 'auto' }}>
            <span>{saveDisplay}</span>
            {currentPage !== PAGE_ROUTES.dashboard &&
              currentPage?.toLowerCase() !== PAGE_ROUTES.faqPage &&
              currentPage !== PAGE_ROUTES.tracker && <BackToDashboardResponsive t={t} />}
            {currentPage?.toLowerCase() !== PAGE_ROUTES.faqPage && currentPage?.toLowerCase() !== PAGE_ROUTES.help && (
              <SupportLinkResponsive />
            )}
          </Box>
        </TopLayer>
      </Wrapper>
    </>
  );

  if (!target) return null;

  return createPortal(content, target, 'floating-header');
}

export default FloatingHeader;
